import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiStar, FiMapPin} = FiIcons;

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Dr. Sarah Chen',
      title: 'Practice Manager',
      company: 'Medical Practice',
      location: 'New York',
      rating: 5,
      quote: 'Our AI voice receptionist has completely transformed how we handle patient calls and appointment scheduling. The system seamlessly integrates with our existing medical practice management software and provides professional 24/7 coverage. Patient satisfaction scores have increased significantly since implementation, and our staff can focus on providing quality care instead of managing phone calls.',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Michael Rodriguez',
      title: 'Senior Partner',
      company: 'Law Firm',
      location: 'New York',
      rating: 5,
      quote: 'The AI-powered receptionist handles our complex legal call routing with exceptional accuracy and professionalism. It understands legal terminology, screens potential clients effectively, and transfers calls to the appropriate attorneys based on practice areas. Our clients consistently comment on the professional service they receive, and we have seen a notable increase in client retention.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
    },
    {
      name: 'Jennifer Thompson',
      title: 'Broker/Owner',
      company: 'Real Estate Agency',
      location: 'New York',
      rating: 5,
      quote: 'As a real estate firm, we cannot afford to miss potential client calls or property inquiries. The AI voice assistant captures leads around the clock and integrates seamlessly with our CRM system. It qualifies prospects, schedules property showings, and provides property information professionally. It is like having a dedicated receptionist that never takes a break.',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            What Our Clients Say About Our AI Voice Technology
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.1}}
            className="text-xl text-gray-600 max-w-4xl mx-auto mb-8"
          >
            Hear from businesses who have transformed their customer service operations with our AI voice receptionist technology. These real success stories demonstrate the impact of professional, intelligent call handling.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-lg text-gray-700 max-w-5xl mx-auto leading-relaxed"
          >
            <p>
              From medical practices to law firms and real estate agencies, our AI voice assistant has helped businesses across diverse industries improve customer satisfaction, capture more leads, and operate more efficiently. These testimonials represent just a few of the hundreds of businesses that have successfully implemented our AI voice receptionist technology.
            </p>
          </motion.div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: index * 0.1}}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              {/* Rating */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              {/* Quote */}
              <blockquote className="text-gray-700 mb-6 leading-relaxed">
                &quot;{testimonial.quote}&quot;
              </blockquote>
              {/* Author Info */}
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face';
                  }}
                />
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                  <div className="text-sm text-gray-600">{testimonial.company}</div>
                  <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                    <SafeIcon icon={FiMapPin} className="w-3 h-3" />
                    {testimonial.location}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.3}}
          className="mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Proven Results Across Multiple Industries
            </h3>
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">Satisfied Business Clients</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">99.9%</div>
                <div className="text-gray-600">System Uptime Guarantee</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                <div className="text-gray-600">Professional Support</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
                <div className="text-gray-600">Languages Supported</div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-700 max-w-3xl mx-auto">
                Our AI voice receptionist technology has processed over 100,000 calls for businesses across various industries, maintaining consistently high customer satisfaction ratings while reducing operational costs and improving efficiency.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;